import Head from "/components/core/head";
import { PureComponent } from "react";
import HeaderScriptsComponent from "/components/core/head/header-scripts-component";
import BodyScriptsComponent from "/components/core/body-scripts/body-scripts-component";
import { GenericConfigContext } from "/contexts/generic-config-context";
import { AbTestsContext } from "/contexts/ab-tests-context";
import throttle from "lodash.throttle";
import { trackEvent } from "/services/bigbrain-service";
import { saveCloudfrontViewerCountryCookie } from "services/cookies/cloudfront-viewer-country-cookies-service";
import { getExactEventNameAbTestVariant } from "services/ab-tests/lp-ab-tests-service";
import {
  getDocumentFullHeight,
  getPassiveIfSupported,
  getScrollPercent,
  getViewportHeight
} from "../../services/dom-utils-service";
import { PAGE_SCROLL_BY_PERCENTAGE } from "/constants/bigbrain-event-types";
import { getRandomInt } from "/services/utils-service";
import BypassBlocksMenuComponent from "components/accessibility/bypass-blocks-menu/bypass-block-menu";
import { fireGtmEvent } from "../../client/services/gtm-service/gtm-service";
import DataLayerEventTypes from "constants/data-layer-event-types";
import PricingVersions from "/constants/pricing-versions";
import { ConditionalWrapper } from "utils/react";
import { CompanyDataProvider } from "client/services/enrichment/company-data";
import { PrefetchSignupAssetsProvider } from "client/services/signup/prefetch-signup-service";
import componentStyles from "./base-template.scss";
import { getUrlParamValue, stripTrailingSlash } from "utils/url";
import classnames from "classnames";
import LiveBoardModalComponent from "components/core/live-board-experience/live-board-modal";
import { LIVE_BOARD_CLOSED, LIVE_BOARD_OPENED } from "constants/bigbrain-event-types";
import { PrefetchTypeformAssetsProvider } from "client/services/typeform/prefetch-typeform-service";
import { PrefetchLiveBoardAssetsProvider } from "client/services/live-board/prefetch-liveboard-service";
import { DEBUG_AB_TEST_PARAM_NAME } from "constants/new-ab-tests";
import { setCookie } from "/services/cookies-service";
import { MULTI_PRODUCTS_TEST_COOKIE_NAME, WEBSITE_AB_TEST_DEBUG_COOKIE_NAME } from "constants/cookies";
import { shouldRenderSecondaryMenu } from "segments/desktop/header/layouts/base-header/base-header-service";
import { StoreUTMDataForEnrichment } from "services/sales-enrichment/sales-enrichment-service";
import { shouldGetTypeformExperience } from "services/typeform/typeform-service";
import { PRICING_PAGE_LINK } from "constants/links";
import { fireAbTestVariantInAccountTestIfNeeded } from "services/monday-platform/existing-accounts-service";
import { enterV13TestIfNeeded } from "services/ab-tests/monetization-ab-tests-service";
import ContactSalesStickyButton from "segments/desktop/core-components/button/contact-sales-sticky-button/contact-sales-sticky-button";
import { shouldGetContactSalesStickyButtonExperience } from "services/ab-tests/acquisition-ab-test-service";

const SCROLL_PERCENTAGE_THROTTLE_TIME = 1000; // Execute a method only once per THROTTLE_TIME
const SCROLL_PERCENTAGE_STEPS = 10;
const UTM_URL_TERM = "utm";
const MULTI_PRODUCT_TESTS_NAMES = ["abc_yourplatform_messaging", "header-products-switcher-test"]; // Add multi products test name here

export default class BaseTemplate extends PureComponent {
  constructor(props) {
    super(props);

    this.pageLoadUnique = getRandomInt(100000000);
    this.passiveIfSupported = getPassiveIfSupported();
    this.scrollPercentageEvent = throttle(this.scrollingByPercentage, SCROLL_PERCENTAGE_THROTTLE_TIME, {
      leading: false,
      trailing: true
    });

    this.state = {
      maxRecordedScrollPercentage: 0,
      showLiveBoard: false,
      showLiveBoardImage: true,
      isFirstFoldVisible: true,
      startShowingContactSalesButton: false,
      liveBoardClosed: false,
      liveBoardProps: {}
    };
  }

  openLiveBoard = (props) => {
    this.setState({ showLiveBoard: true, showLiveBoardImage: false, liveBoardProps: props });
    trackEvent(LIVE_BOARD_OPENED);
  };

  setIsFirstFoldVisible = (state) => {
    this.setState({ isFirstFoldVisible: state });
  };

  setStartShowingContactSalesButton = (state) => {
    this.setState({ startShowingContactSalesButton: state });
  };

  closeLiveBoard = () => {
    this.setState({ showLiveBoard: false, showLiveBoardImage: true, liveBoardClosed: true });
    trackEvent(LIVE_BOARD_CLOSED);
  };

  scrollingByPercentage = async () => {
    const { maxRecordedScrollPercentage } = this.state;
    try {
      const currentScrollPercentage = await getScrollPercent();
      const documentHeight = await getDocumentFullHeight();
      const viewportHeight = await getViewportHeight();
      if (currentScrollPercentage > maxRecordedScrollPercentage + SCROLL_PERCENTAGE_STEPS) {
        this.setState({ maxRecordedScrollPercentage: currentScrollPercentage });
        trackEvent(PAGE_SCROLL_BY_PERCENTAGE, {
          kind: parseInt(currentScrollPercentage),
          info1: viewportHeight,
          info2: documentHeight,
          info3: this.pageLoadUnique //	for easily find the last event per page load
        });
      }
    } catch (e) {}
  };

  componentDidMount() {
    const {
      cloudfrontViewerCountry,
      persistantCookies,
      abTestVariants,
      isPricingPage,
      forcePricing,
      isPreview,
      isV13PricingVersion
    } = this.props;

    window.addEventListener("scroll", this.scrollPercentageEvent, this.passiveIfSupported);
    saveCloudfrontViewerCountryCookie(cloudfrontViewerCountry);
    if (window?.location?.href?.includes(UTM_URL_TERM)) {
      StoreUTMDataForEnrichment();
    }
    this.sendGenericAbTestEventIfNeeded();
    this.setAbTestDebugCookieIfNeeded();
    this.setMultiProductsTestCookieIfNeeded();

    if (isPricingPage && !isPreview && persistantCookies) {
      enterV13TestIfNeeded(abTestVariants, persistantCookies, forcePricing, isV13PricingVersion);
    }
  }

  setAbTestDebugCookieIfNeeded = () => {
    const { asPath } = this.props;
    const abTestDebugParamValue = getUrlParamValue(asPath, DEBUG_AB_TEST_PARAM_NAME);
    if (abTestDebugParamValue) {
      setCookie(WEBSITE_AB_TEST_DEBUG_COOKIE_NAME, abTestDebugParamValue, {
        expires: 1 / 24 // 1 hour
      });
    }
  };

  setMultiProductsTestCookieIfNeeded = () => {
    const { abTestVariants } = this.props;
    const multiProductsTest = abTestVariants?.find((abTest) => MULTI_PRODUCT_TESTS_NAMES.includes(abTest.name));

    if (multiProductsTest) {
      setCookie(
        MULTI_PRODUCTS_TEST_COOKIE_NAME,
        JSON.stringify({
          id: multiProductsTest.id,
          variant: multiProductsTest.versionNum
        }),
        {
          expires: 1 // 24 hours
        }
      );
    }
  };

  sendGenericAbTestEventIfNeeded() {
    const { abTestVariants, abTestExactEventName } = this.props;
    const abTestVariant = getExactEventNameAbTestVariant(abTestExactEventName, abTestVariants);

    if (abTestExactEventName && abTestVariant) {
      // TODO: should move to head script (AbTestSplitEventScript) and remove this method. Right now it depended on initialization functions and 3rd party services that should also move to head script
      fireGtmEvent(DataLayerEventTypes.IN_PAGE_AB_TEST_EVENT_NAME, {
        abTestName: abTestExactEventName,
        abTestVariant
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.scrollPercentageEvent, this.passiveIfSupported);
  }

  translate = (key) => {
    const { translations } = this.props;
    return translations[key] || key;
  };

  translateWithParam = (key, parameter1) => {
    const translation = this.translate(key);
    const templateString = translation.replace("%param1%", parameter1);
    return templateString;
  };

  translateOrTranslateWithParam = (keyToTranslate) => {
    if (!keyToTranslate) return null;
    if (typeof keyToTranslate === "object") {
      const { key, param } = keyToTranslate;
      return this.translateWithParam(key, param);
    }
    return this.translate(keyToTranslate);
  };

  render() {
    const {
      children,
      title,
      description,
      canonicalUrl,
      asPath,
      fontLazyLoad,
      showHeaderScripts,
      marketingTemplateBoardIds,
      showTrustpilotReviews,
      coupon,
      extendTrialCookie,
      clusterId,
      pageProductId,
      pageClusterId,
      pageSubClusterId,
      pageClusterConfig,
      subClusterId,
      clusterIdSource,
      subClusterIdSource,
      clusterConfig,
      isClusterMiniSitePage,
      useProductMiniSiteConfig,
      localeId,
      userSelectedLanguage,
      overrideLanguages,
      withFullstory,
      fullstoryRecordingFrequency,
      abTestVariants,
      abTestExactEventName,
      template,
      useExpandingSignup,
      disableGTM,
      isMobileOrTabletBrowser,
      isMobileBrowser,
      isTypeformDisabled,
      imagesLazyLoad,
      noIndex,
      noFollow,
      gtmContainerId,
      useSectionsMenu,
      isPreview,
      currency,
      cloudfrontViewerCountry,
      enableDriftChatBot,
      driftDelayedLoadMilliseconds,
      driftOpenChatDialogOnLaunch,
      driftPreventLoadOnLaunch,
      enableHotjar,
      hotjarFinalDate,
      pricingVersion,
      hasFreeTier,
      hideBannerWithBackground,
      cookies, // bad practice - should move needed cookies to persistantCookies (they are aligned in cloudFront cache ass well)
      persistantCookies,
      lazyImages,
      grantedFeatures,
      freeTierForcePrevent,
      skipClustersQuestion,
      forcePricing,
      forceCurrency,
      hasStudentPlan,
      facebookMetaOverride,
      twitterMetaOverride,
      enablePageUsageDurationTracking,
      dynamicAssetParam,
      activePagePath,
      activeOriginalPagePath,
      loadCompanyDetails,
      localizedPages,
      pageBackgroundColorName,
      is404Page,
      isPricingPage,
      testTypeformContactSales,
      forceTypeformContactSalesExperience,
      sendToSignupWithProductPage,
      seoFAQ,
      addLiveBoardExperience,
      preloadFirstFoldImages,
      contactSalesTestMode,
      shouldGetSignupPrefetchedAssets,
      signupDynamicAssetsLinks,
      embeddedBoardLink,
      forceProductSignup,
      customHeadScript,
      customBodyScript,
      getMobileSoftSignup,
      useNewTypeformUSVariant,
      isV13PricingVersion,
      segments,
      isWMHighIntentExperience,
      withContactSalesStickyButton,
      simulateStickyButtonTest
    } = this.props;

    const {
      showLiveBoard,
      showLiveBoardImage,
      liveBoardClosed,
      liveBoardProps,
      isFirstFoldVisible,
      startShowingContactSalesButton
    } = this.state;
    const isSecondaryMenuNeeded = shouldRenderSecondaryMenu(
      isClusterMiniSitePage,
      pageClusterConfig,
      useProductMiniSiteConfig
    );

    const shouldGetTypeformPrefetchedAssets = shouldGetTypeformExperience({
      abTests: abTestVariants,
      isTypeformDisabled,
      cloudfrontViewerCountry,
      testTypeformContactSales,
      forceTypeformContactSalesExperience
    });

    const prefetchTypeformAssetsProviderProps = {
      cloudfrontViewerCountry,
      useNewTypeformUSVariant,
      abTests: abTestVariants
    };

    function extractTypes(data) {
      return data?.map((item) => item.type);
    }

    return (
      <GenericConfigContext.Provider
        value={{
          localeId,
          clusterId,
          pageProductId,
          pageClusterId,
          pageSubClusterId,
          pageClusterConfig,
          subClusterId,
          clusterConfig,
          isClusterMiniSitePage,
          useProductMiniSiteConfig,
          userSelectedLanguage,
          overrideLanguages,
          useExpandingSignup,
          isMobileOrTabletBrowser,
          isMobileBrowser,
          imagesLazyLoad,
          noIndex,
          noFollow,
          isPreview,
          hasFreeTier,
          isTypeformDisabled,
          pricingVersion: isPreview ? PricingVersions.DEFAULT_VERSION : pricingVersion,
          cloudfrontViewerCountry,
          lazyImages,
          translate: this.translate,
          translateWithParam: this.translateWithParam,
          translateOrTranslateWithParam: this.translateOrTranslateWithParam,
          hideBannerWithBackground,
          cookies,
          persistantCookies,
          currency,
          grantedFeatures,
          freeTierForcePrevent,
          forcePricing,
          forceCurrency,
          hasStudentPlan,
          dynamicAssetParam,
          activePagePath,
          activeOriginalPagePath,
          useSectionsMenu,
          is404Page,
          isPricingPage,
          testTypeformContactSales,
          forceTypeformContactSalesExperience,
          sendToSignupWithProductPage,
          isSecondaryMenuNeeded,
          contactSalesTestMode,
          addLiveBoardExperience,
          openLiveBoard: this.openLiveBoard,
          showLiveBoardImage,
          liveBoardClosed,
          forceProductSignup,
          getMobileSoftSignup,
          useNewTypeformUSVariant,
          isV13PricingVersion,
          isWMHighIntentExperience,
          setIsFirstFoldVisible: this.setIsFirstFoldVisible,
          setStartShowingContactSalesButton: this.setStartShowingContactSalesButton
        }}
      >
        <AbTestsContext.Provider
          value={{
            abTestVariants,
            abTestExactEventName
          }}
        >
          <ConditionalWrapper
            condition={loadCompanyDetails}
            wrapper={(children) => <CompanyDataProvider>{children}</CompanyDataProvider>}
          >
            <ConditionalWrapper
              condition={shouldGetSignupPrefetchedAssets}
              wrapper={(children) => (
                <PrefetchSignupAssetsProvider signupDynamicAssetsLinks={signupDynamicAssetsLinks}>
                  {children}
                </PrefetchSignupAssetsProvider>
              )}
            >
              <ConditionalWrapper
                condition={shouldGetTypeformPrefetchedAssets}
                wrapper={(children) => (
                  <PrefetchTypeformAssetsProvider {...prefetchTypeformAssetsProviderProps}>
                    {children}
                  </PrefetchTypeformAssetsProvider>
                )}
              >
                <ConditionalWrapper
                  condition={addLiveBoardExperience}
                  wrapper={(children) => <PrefetchLiveBoardAssetsProvider>{children}</PrefetchLiveBoardAssetsProvider>}
                >
                  <>
                    <Head
                      title={title}
                      description={description}
                      canonicalUrl={canonicalUrl}
                      seoFAQ={seoFAQ}
                      fontLazyLoad={fontLazyLoad}
                      template={template}
                      disableGTM={disableGTM}
                      gtmContainerId={gtmContainerId}
                      isMobileOrTabletBrowser={isMobileOrTabletBrowser}
                      pagePath={asPath}
                      facebookMetaOverride={facebookMetaOverride}
                      twitterMetaOverride={twitterMetaOverride}
                      localizedPages={localizedPages}
                      shouldGetSignupPrefetchedAssets={shouldGetSignupPrefetchedAssets}
                      shouldGetTypeformPrefetchedAssets={shouldGetTypeformPrefetchedAssets}
                      addLiveBoardExperience={addLiveBoardExperience}
                      preloadFirstFoldImages={preloadFirstFoldImages}
                    />

                    {showHeaderScripts && (
                      <HeaderScriptsComponent
                        coupon={coupon}
                        extendTrialCookie={extendTrialCookie}
                        marketingTemplateBoardIds={marketingTemplateBoardIds}
                        clusterId={clusterId}
                        subClusterId={subClusterId}
                        clusterIdSource={clusterIdSource}
                        subClusterIdSource={subClusterIdSource}
                        localeId={localeId}
                        template={template}
                        skipClustersQuestion={skipClustersQuestion}
                        enablePageUsageDurationTracking={enablePageUsageDurationTracking}
                        customHeadScript={customHeadScript}
                        segmentsList={extractTypes(segments)}
                      />
                    )}

                    {!isPreview && (
                      <div className="accessibility-menu-wrapper">
                        <BypassBlocksMenuComponent />
                      </div>
                    )}

                    <div
                      className={classnames("template-content-wrapper", {
                        "with-background-color": !!pageBackgroundColorName
                      })}
                    >
                      {children}
                      {addLiveBoardExperience && (
                        <>
                          <div className="live-board-popup-wrapper">
                            <LiveBoardModalComponent
                              showLiveBoard={showLiveBoard}
                              closeLiveBoard={this.closeLiveBoard}
                              translate={this.translate}
                              openPeekToBoard={this.openPeekToBoard}
                              closePeekToBoard={this.closePeekToBoard}
                              embeddedBoardLink={embeddedBoardLink}
                              {...liveBoardProps}
                            />
                          </div>
                        </>
                      )}
                      {withContactSalesStickyButton &&
                        (simulateStickyButtonTest ||
                          shouldGetContactSalesStickyButtonExperience(abTestVariants, true)) && (
                          <div className="contact-sales-sticky-button-wrapper">
                            <ContactSalesStickyButton
                              showButton={startShowingContactSalesButton && !isFirstFoldVisible}
                            />
                          </div>
                        )}
                    </div>
                    {showHeaderScripts && (
                      <BodyScriptsComponent
                        withFullstory={withFullstory}
                        fullstoryRecordingFrequency={fullstoryRecordingFrequency}
                        showTrustpilotReviews={showTrustpilotReviews}
                        pagePath={asPath}
                        fontLazyLoad={fontLazyLoad}
                        template={template}
                        disableGTM={disableGTM}
                        gtmContainerId={gtmContainerId}
                        enableDriftChatBot={enableDriftChatBot}
                        driftDelayedLoadMilliseconds={driftDelayedLoadMilliseconds}
                        driftOpenChatDialogOnLaunch={driftOpenChatDialogOnLaunch}
                        driftPreventLoadOnLaunch={driftPreventLoadOnLaunch}
                        enableHotjar={enableHotjar}
                        hotjarFinalDate={hotjarFinalDate}
                        clusterId={clusterId}
                        hasFreeTier={hasFreeTier}
                        localeId={localeId}
                        subClusterId={subClusterId}
                        isMobileOrTabletBrowser={isMobileOrTabletBrowser}
                        loadCompanyDetails={loadCompanyDetails}
                        is404Page={is404Page}
                        customBodyScript={customBodyScript}
                      />
                    )}
                    <style jsx>{componentStyles}</style>
                  </>
                </ConditionalWrapper>
              </ConditionalWrapper>
            </ConditionalWrapper>
          </ConditionalWrapper>
        </AbTestsContext.Provider>
      </GenericConfigContext.Provider>
    );
  }
}

BaseTemplate.defaultProps = {
  showHeaderScripts: true,
  withFullstory: false,
  useExpandingSignup: false,
  translations: {},
  isPreview: false,
  skipClustersQuestion: false
};
